import React from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, Briefcase, Smile, Sparkles } from 'lucide-react';
import { COMPANY } from '../lib/company';

const Pillar = ({ icon: Icon, title, children }) => (
  <div className="flex gap-4">
    <div className="flex-shrink-0 w-10 h-10 rounded-full bg-[#1F3D2B]/5 flex items-center justify-center">
      <Icon className="w-4 h-4 text-[#1F3D2B]" />
    </div>
    <div>
      <p className="font-medium text-[#1F3D2B] mb-1">{title}</p>
      <p className="text-sm text-stone-600 leading-relaxed">{children}</p>
    </div>
  </div>
);

const AboutPage = () => {
  return (
    <div className="min-h-[80vh] bg-stone-50 py-16 px-4" data-testid="about-page">
      <div className="max-w-3xl mx-auto">
        {/* Eyebrow + headline */}
        <div className="text-center mb-12">
          <div className="inline-block px-4 py-1 bg-[#1F3D2B]/5 text-[#1F3D2B] text-xs tracking-[0.2em] uppercase rounded-full mb-3">
            About VinePass
          </div>
          <h1
            className="font-serif text-4xl sm:text-5xl text-[#1F3D2B] mb-4"
            data-testid="about-heading"
          >
            Curated experiences, made effortless.
          </h1>
          <p
            className="text-[#1F3D2B] font-medium max-w-xl mx-auto leading-relaxed"
            data-testid="about-intro"
          >
            VinePass is a premium ticketing platform designed for curated experiences.
          </p>
        </div>

        {/* Long-form story */}
        <div
          className="bg-white border border-stone-200 rounded-2xl shadow-sm px-8 sm:px-12 py-10 mb-10 space-y-6"
          data-testid="about-body"
        >
          <p className="text-stone-700 leading-relaxed">
            We exist to simplify how wine farms, event organisers, and experience hosts create,
            manage, and sell their events — all while delivering a seamless and elevated
            experience for their guests.
          </p>
          <p className="text-stone-700 leading-relaxed">
            From vineyard picnics and wine tastings to sunset concerts and private events,
            VinePass brings together exceptional experiences in one place.
          </p>
        </div>

        {/* Focus pillars */}
        <div className="bg-white border border-stone-200 rounded-2xl shadow-sm px-8 sm:px-12 py-10 mb-10">
          <p className="text-xs uppercase tracking-[0.2em] text-[#C9A27A] mb-6 text-center">
            Our Focus
          </p>
          <div className="space-y-6">
            <Pillar icon={Briefcase} title="Effortless for organisers">
              From event setup to payouts and guest lists, we cover the full lifecycle so hosts
              can spend less time on logistics and more time on hospitality.
            </Pillar>
            <Pillar icon={Smile} title="Smooth for guests">
              An intuitive booking flow, beautifully delivered tickets, and a single place to
              discover experiences worth showing up for.
            </Pillar>
            <Pillar icon={Sparkles} title="Elevated by design">
              We&apos;re raising the standard of how experiences are discovered and attended —
              curation first, ticketing second.
            </Pillar>
          </div>
        </div>

        {/* Closing paragraph */}
        <div
          className="bg-[#1F3D2B] text-white rounded-2xl px-8 sm:px-12 py-10 text-center mb-10"
          data-testid="about-closing"
        >
          <p className="text-white/90 leading-relaxed mb-6">
            Built with a deep understanding of the wine and hospitality space, VinePass is more
            than just a ticketing platform — it&apos;s a tool designed to help experiences thrive.
          </p>
          <p
            className="font-serif text-2xl sm:text-3xl text-[#C9A27A] leading-snug"
            data-testid="about-tagline"
          >
            Discover experiences. Create moments. Experience VinePass.
          </p>
        </div>

        {/* CTAs */}
        <div className="flex flex-col sm:flex-row gap-3 justify-center">
          <Link
            to="/events"
            className="inline-flex items-center justify-center gap-2 bg-[#1F3D2B] text-white px-6 py-3 rounded-full text-sm hover:bg-[#172E20] transition"
            data-testid="about-cta-events"
          >
            Browse Experiences <ArrowRight className="w-4 h-4" />
          </Link>
          <Link
            to="/fees"
            className="inline-flex items-center justify-center gap-2 border border-stone-300 text-[#1F3D2B] px-6 py-3 rounded-full text-sm hover:bg-stone-100 transition"
            data-testid="about-cta-fees"
          >
            Pricing &amp; Fees
          </Link>
        </div>

        {/* Registered company details */}
        <div
          className="bg-white border border-stone-200 rounded-2xl shadow-sm px-8 sm:px-12 py-8 mt-10 space-y-3"
          data-testid="about-company-details"
        >
          <p className="text-xs uppercase tracking-[0.2em] text-[#C9A27A] text-center">
            About the Company
          </p>
          <p className="text-stone-700 leading-relaxed text-sm">
            VinePass is a South African ticketing and event management platform built for curated
            experiences, wine farms, and event organisers. VinePass is operated by{' '}
            <span className="font-semibold text-[#1F3D2B]">{COMPANY.legalName}</span>, a company
            registered with the Companies and Intellectual Property Commission (CIPC) in{' '}
            {COMPANY.country}.
          </p>
          <p
            className="text-stone-700 leading-relaxed text-sm"
            data-testid="about-company-registration"
          >
            Company Registration Number:{' '}
            <span className="font-semibold text-[#1F3D2B]">{COMPANY.registrationNumber}</span>
          </p>
        </div>
      </div>
    </div>
  );
};

export default AboutPage;
